/**
 * Client-side cache for hosted-page checkout sessions so a page refresh reuses
 * a still-valid session instead of minting a new one each time.
 *
 * - Scoped per tab via sessionStorage (survives refresh, clears on tab close).
 * - Keyed by tenant + hosted page config id.
 * - The cached id is always re-validated server-side before reuse; this cache
 *   is only an optimization, never a source of truth.
 */
const TTL_MS = 15 * 60 * 1000;

const keyFor = (configId) => {
  const tenantId = window.location.hostname.split(".")[0];
  return `altruon_hp_session:${tenantId}:${configId}`;
};

export const getCachedSession = (configId) => {
  if (!configId) return null;
  try {
    const raw = sessionStorage.getItem(keyFor(configId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.sessionId || !parsed?.expiresAt) return null;
    if (Date.now() >= parsed.expiresAt) {
      sessionStorage.removeItem(keyFor(configId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const setCachedSession = (
  configId,
  sessionId,
  createdAt = Date.now(),
  expiresAt = null
) => {
  if (!configId || !sessionId) return;
  try {
    const exp = expiresAt ?? createdAt + TTL_MS;
    sessionStorage.setItem(
      keyFor(configId),
      JSON.stringify({ sessionId, createdAt, expiresAt: exp })
    );
  } catch {
    // storage unavailable (private mode / quota) — reuse is simply skipped
  }
};

export const clearCachedSession = (configId) => {
  if (!configId) return;
  try {
    sessionStorage.removeItem(keyFor(configId));
  } catch {
    // ignore
  }
};
